import { Button } from '@mediahuis/chameleon-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '../hooks/useQueryParams';
import { store } from '../redux/store';
import { buildInviteSwitchUrl } from '../utils/switchAccount';

interface OtherAccountButtonProps {
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'lg';
  appearance?: 'primary' | 'secondary' | 'tertiary';
  className?: string;
  style?: Record<string, string | number>;
  onClick?: () => void;
  label?: string;
  screen?: string;
  /**
   * Switch account by re-authenticating (`prompt=select_account`) instead of logging out.
   * Opt-in per call site: `/invite` sets it, the `/invite-error` buttons do not — see
   * `buildInviteSwitchUrl` (GC-16441).
   */
  reauthenticate?: boolean;
}

export function OtherAccountButton({
  disabled = false,
  fullWidth = true,
  size = 'lg',
  appearance = 'tertiary',
  className,
  style,
  onClick,
  label,
  screen,
  reauthenticate = false,
}: OtherAccountButtonProps) {
  const { t } = useTranslation();
  const { inviteId, language, brand } = useQueryParams();
  const getSrsApiConfig = () => store.getState().apiConfig.srs;
  const config = getSrsApiConfig();

  const handleOtherAccount = () => {
    // Call optional onClick callback for tracking
    if (onClick) {
      onClick();
    }

    const url = buildInviteSwitchUrl({
      reauthenticate,
      invitationRoot: config.invitationRoot,
      inviteId,
      language,
      screen,
      brand,
    });
    if (!url) return;

    window.location.href = url;
  };

  return (
    <Button
      size={size}
      appearance={appearance}
      data-cy="otherAccountButton"
      fullWidth={fullWidth}
      className={className}
      style={style}
      onClick={handleOtherAccount}
      disabled={disabled}>
      {label ?? t('invitation.accept.otherAccountButtonText')}
    </Button>
  );
}
